import { Component, ErrorInfo, ReactNode } from 'react';
import { Location, useLocation } from 'react-router';

import ErrorPage from '../../pages/ErrorPage/ErrorPage';
import AppError from '../../state/AppError';

interface ErrorBoundaryProps {
  children: ReactNode;
  location: Location;
}

interface ErrorBoundaryState {
  error: Nullable<Error>;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      error: null,
    };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error: error };
  }

  componentDidUpdate(prevProps: ErrorBoundaryProps) {
    if (!this.state.error) {
      return;
    }

    if (prevProps.location.pathname !== this.props.location.pathname || prevProps.children !== this.props.children) {
      this.setState({
        error: null,
      });
    }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // todo: error logging
    console.warn(error, info);
  }

  render() {
    if (this.state.error) {
      return (
        <ErrorPage error={this.state.error instanceof AppError ? this.state.error : AppError.createDefaultError()} />
      );
    }

    return this.props.children;
  }
}

function ErrorBoundaryWithLocation(props: Pick<ErrorBoundaryProps, 'children'>) {
  const location = useLocation();

  return <ErrorBoundary location={location}>{props.children}</ErrorBoundary>;
}

export default ErrorBoundaryWithLocation;
